import React, { useEffect, useRef } from "react";
import "./CsiNeural.css";

/* ═══════════════════════════════════════════════════════════════
   CsiNeural — an animated neural-network mesh on <canvas>.
   Nodes on a jittered grid, connected only to near neighbours
   (long crossing edges read as noise, not structure), with
   signal pulses travelling the connections.

   No images, no dependencies. Sized off its container, so it
   fills whatever panel you put it in.

   Props:
     opacity   0.5   overall strength
     density   1     node count multiplier
     speed     1     pulse speed
   ═══════════════════════════════════════════════════════════════ */

export default function CsiNeural({
  opacity = 0.5,
  density = 1,
  speed = 1,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let w = 0;
    let h = 0;
    let dpr = 1;
    let nodes = [];
    let edges = [];

    /* deterministic RNG so the mesh doesn't reshuffle on every resize */
    const mulberry = (seed) => () => {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };

    const build = () => {
      const rnd = mulberry(11);
      const ar = h / Math.max(w, 1);

      /* keep cells roughly square whatever the panel shape */
      const cols = Math.max(3, Math.round(4.4 * density));
      const rows = Math.max(3, Math.round(cols * ar * 1.05));

      nodes = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          nodes.push({
            x: (c + 0.5) / cols + (rnd() - 0.5) * 0.11,
            y: (r + 0.5) / rows + (rnd() - 0.5) * 0.09,
            r: 1.3 + rnd() * 1.9,
            ph: rnd() * Math.PI * 2, // breathing offset
            dx: (rnd() - 0.5) * 0.0016, // slow drift
            dy: (rnd() - 0.5) * 0.0016,
            bx: 0,
            by: 0,
          });
        }
      }
      nodes.forEach((n) => {
        n.bx = n.x;
        n.by = n.y;
      });

      /* two nearest neighbours each, distance-capped */
      edges = [];
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const cand = [];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = (a.y - b.y) * ar;
          cand.push([Math.hypot(dx, dy), j]);
        }
        cand.sort((p, q) => p[0] - q[0]);
        for (let k = 0; k < Math.min(2, cand.length); k++) {
          if (cand[k][0] < 0.46) {
            edges.push({ a: i, b: cand[k][1], off: rnd(), rate: 0.1 + rnd() * 0.13 });
          }
        }
      }
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(rect.width, 1);
      h = Math.max(rect.height, 1);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let raf = 0;
    let running = true;
    const t0 = performance.now();

    const draw = (now) => {
      const t = reduced ? 4 : ((now - t0) / 1000) * speed;
      ctx.clearRect(0, 0, w, h);

      /* drift: nodes wander a little around their grid position */
      for (const n of nodes) {
        n.x = n.bx + Math.sin(t * 0.22 + n.ph) * n.dx * 34;
        n.y = n.by + Math.cos(t * 0.19 + n.ph) * n.dy * 34;
      }

      /* connections */
      ctx.lineWidth = 1;
      for (const e of edges) {
        const a = nodes[e.a];
        const b = nodes[e.b];
        ctx.strokeStyle = "rgba(96,150,245,0.20)";
        ctx.beginPath();
        ctx.moveTo(a.x * w, a.y * h);
        ctx.lineTo(b.x * w, b.y * h);
        ctx.stroke();
      }

      /* travelling signals — a comet head with a short tail */
      for (const e of edges) {
        const a = nodes[e.a];
        const b = nodes[e.b];
        const p = (t * e.rate + e.off) % 1;
        const x1 = a.x * w;
        const y1 = a.y * h;
        const x2 = b.x * w;
        const y2 = b.y * h;
        for (let s = 0; s < 8; s++) {
          const q = p - s * 0.022;
          if (q < 0) break;
          const qx = x1 + (x2 - x1) * q;
          const qy = y1 + (y2 - y1) * q;
          ctx.fillStyle = `rgba(150,200,255,${0.55 * (1 - s / 8)})`;
          ctx.beginPath();
          ctx.arc(qx, qy, 1.7 * (1 - s / 12), 0, Math.PI * 2);
          ctx.fill();
        }
      }

      /* nodes — soft halo, bright core, gentle breathing */
      for (const n of nodes) {
        const x = n.x * w;
        const y = n.y * h;
        const pulse = 0.82 + 0.18 * Math.sin(t * 1.1 + n.ph);
        const r = n.r * pulse;

        const g = ctx.createRadialGradient(x, y, 0, x, y, r * 5.5);
        g.addColorStop(0, "rgba(120,175,255,0.30)");
        g.addColorStop(1, "rgba(120,175,255,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r * 5.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = `rgba(186,218,255,${0.75 * pulse})`;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (running && !reduced) raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    const onVis = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [density, speed]);

  return (
    <div
      className={`csi-neural ${className}`.trim()}
      style={{ opacity }}
      aria-hidden="true"
    >
      <canvas ref={canvasRef} className="csi-neural__canvas" />
    </div>
  );
}
